import { useMutation, useQueryClient } from "@tanstack/react-query";

import { react } from "@/adapters/api";
import {
	type ReactionType,
	toReactionType,
	toReactionTypes,
} from "@/features/feed/reactions.ts";
import type { Post } from "@/features/feed/types.ts";
import { updatePostCaches } from "@/features/feed/utils/cache.ts";
import {
	applyPostReaction,
	setPostReaction,
} from "@/features/feed/utils/social.ts";

export function useReact(postId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (type: ReactionType) => react(postId, type),
		onMutate: async (type: ReactionType) => {
			await queryClient.cancelQueries({ queryKey: ["posts"] });
			await queryClient.cancelQueries({ queryKey: ["post", postId] });
			const listSnapshots = queryClient.getQueriesData<Post[]>({
				queryKey: ["posts"],
			});
			const postSnapshots = queryClient.getQueriesData<Post>({
				queryKey: ["post", postId],
			});
			updatePostCaches(queryClient, postId, (post) =>
				applyPostReaction(post, type),
			);
			return { snapshots: [...listSnapshots, ...postSnapshots] };
		},
		onError: (_error, _input, context) => {
			for (const [queryKey, data] of context?.snapshots ?? []) {
				queryClient.setQueryData(queryKey, data);
			}
		},
		onSuccess: (resp) => {
			updatePostCaches(queryClient, postId, (post) =>
				setPostReaction(post, {
					count: resp.count,
					reacted: resp.reacted,
					type: toReactionType(resp.type),
					types: toReactionTypes(resp.types),
				}),
			);
		},
	});
}
